// src/components/Tools/Results/WappalyzerResults.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ResultRendererProps } from './types';

type Tech = {
  name?: string;
  version?: string;
  slug?: string;
  confidence?: number | string | null;
  categories?: string[];
  icon?: string;
  description?: string;
};

type PayloadByCategory = {
  url?: string;
  byCategory?: Record<string, Tech[]>;
};

type PayloadFlat = {
  url?: string;
  technologies?: Tech[];
};

type Norm = {
  url: string;
  byCategory: Record<string, Tech[]>;
};

function chunkArray<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

function isCriticalTechnology(technologyName?: string): boolean {
  const critical = ['SSL','TLS','OAuth','JWT','WAF','CDN','DDoS Protection','Firewall','Antivirus','IDS','IPS'];
  return technologyName ? critical.includes(technologyName) : false;
}

const getIconPath = (icon?: string): string => {
  if (!icon) return '/assets/icons/default.svg';
  const normalized = icon.trim();
  const lower = normalized.toLowerCase();
  const fileName = normalized.split('/').pop() || normalized;
  if (lower.endsWith('.png')) return `/assets/icons/converted/${fileName}`;
  if (lower.endsWith('.svg')) return `/assets/icons/${fileName}`;
  return '/assets/icons/default.svg';
};

function normalize(results: any[]): Norm {
  if (!Array.isArray(results) || results.length === 0) {
    return { url: '', byCategory: {} };
  }

  const first = results[0];

  // 1) Ya viene agrupado por categorías
  if (first && typeof first === 'object' && 'byCategory' in first) {
    const url = String((first as PayloadByCategory).url || '');
    const byCategoryRaw = (first as PayloadByCategory).byCategory || {};
    const byCategory: Record<string, Tech[]> = {};
    for (const [cat, list] of Object.entries(byCategoryRaw)) {
      byCategory[cat] = Array.isArray(list) ? list.map(t => ({ ...t })) : [];
    }
    return { url, byCategory };
  }

  // 2) Viene plano: { url, technologies }
  if (first && typeof first === 'object' && ('technologies' in first || 'url' in first)) {
    const url = String((first as PayloadFlat).url || '');
    const tf = (first as PayloadFlat).technologies;
    const techs: Tech[] = Array.isArray(tf) ? (tf as Tech[]) : []; // << TIPADO EXPLÍCITO

    const byCategory: Record<string, Tech[]> = {};
    for (const t of techs) {
      const cats = Array.isArray(t.categories) && t.categories.length ? t.categories : ['Other'];
      for (const c of cats) {
        if (!byCategory[c]) byCategory[c] = [];
        byCategory[c].push(t);
      }
    }
    return { url, byCategory };
  }

  // 3) Fallback: results ya es un array de tecnologías
  const techs = results as Tech[];
  const byCategory: Record<string, Tech[]> = {};
  for (const t of techs) {
    const cats = Array.isArray(t.categories) && t.categories.length ? t.categories : ['Other'];
    for (const c of cats) {
      if (!byCategory[c]) byCategory[c] = [];
      byCategory[c].push(t);
    }
  }
  return { url: '', byCategory };
}

const WappalyzerResults: React.FC<ResultRendererProps> = ({ results }) => {
  const { t } = useTranslation();
  const payload = React.useMemo(() => normalize(results), [results]);
  const entries = Object.entries(payload.byCategory);

  if (entries.length === 0) {
    return <p className="text-sm text-slate-500">{t('tools.results_scan.wappalyzer.empty')}</p>;
  }

  const chunks = chunkArray(entries, 2);

  return (
    <div className="w-full space-y-6">
      {payload.url && (
        <div className="text-sm text-slate-700">
          <span className="font-semibold text-slate-900">{t('tools.results_scan.wappalyzer.url')}</span>
          <span className="mx-1 text-slate-400">•</span>
          <span className="break-all">{payload.url}</span>
        </div>
      )}

      {chunks.map((chunk, idx) => (
        <div key={idx} className="flex flex-wrap -mx-2">
          {chunk.map(([categoryName, techs]) => (
            <div key={categoryName} className="w-full md:w-1/2 px-2 mb-4">
              <h4 className="text-2xl font-semibold mb-2">{categoryName}</h4>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {techs.map((tech, i) => {
                  const iconPath = getIconPath(tech.icon);
                  const name = tech.name || '—';

                  return (
                    <div
                      key={`${categoryName}-${name}-${i}`}
                      className={`flex flex-col items-center p-4 rounded-lg ${
                        isCriticalTechnology(name) ? 'bg-yellow-100' : 'bg-transparent hover:bg-gray-100'
                      }`}
                    >
                      <img
                        src={iconPath}
                        alt={name}
                        className="mb-2 h-16 w-16 object-contain"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = '/assets/icons/default.svg';
                        }}
                      />
                      <div className="flex items-center space-x-1">
                        <span className="text-center font-bold">{name}</span>
                        {tech.description && (
                          <div className="relative group">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4 text-gray-500 cursor-pointer"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 110 20 10 10 0 010-20z" />
                            </svg>
                            <div
                              className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg z-10"
                              style={{ textAlign: 'justify' }}
                            >
                              {tech.description}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default WappalyzerResults;
